/**
 * COIUploader — Drag-drop Certificate of Incorporation upload
 * ===========================================================
 * 1. User drops a PDF/JPG of a COI
 * 2. Backend OCRs via Claude vision → CIN extraction
 * 3. Auto-triggers MCA21 CIN-direct lookup
 * 4. Notifies parent with result so parent can render the existing MCA21 result UI
 */
import React, { useState, useRef } from "react";
import axios from "axios";
import { UploadCloud, FileText, Loader2, CheckCircle2, AlertTriangle, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/coi`;
const MAX_MB = 15;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp";

export default function COIUploader({ onMcaResult }) {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState("");
  const [extraction, setExtraction] = useState(null);
  const [filename, setFilename] = useState("");
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB > ${MAX_MB}MB)`);
      return;
    }
    const okType = ["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type);
    if (!okType) {
      toast.error(`Unsupported file type. Use PDF / JPG / PNG / WEBP.`);
      return;
    }

    setFilename(file.name);
    setExtraction(null);
    setUploading(true);
    setStep("Uploading certificate...");

    const fd = new FormData();
    fd.append("file", file);
    fd.append("auto_lookup", "true");

    try {
      setStep("Reading text with Claude Vision...");
      const { data } = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtraction(data.extraction || null);

      if (data.extraction?.cin) {
        setStep(`CIN extracted — running MCA21 lookup for ${data.extraction.cin}...`);
        toast.success(`CIN ${data.extraction.cin} extracted from "${file.name}"`);
        if (data.mca21 && onMcaResult) {
          onMcaResult({
            result: data.mca21.result,
            risk: data.mca21.risk,
            cin: data.extraction.cin,
            company_name: data.mca21.result?.company_name || data.extraction.company_name,
          });
        }
      } else {
        toast.error(data.extraction?.notes || "No CIN found in this document");
        setStep("");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "COI upload failed");
      setStep("");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const reset = () => {
    setExtraction(null);
    setFilename("");
    setStep("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div data-testid="coi-uploader" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 18, marginBottom: 16 }}>
      <div className="flex items-center gap-2 mb-3">
        <UploadCloud style={{ width: 18, height: 18, color: "#a855f7" }} />
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "#f1f5f9" }}>Upload Certificate of Incorporation</h3>
        <span style={{ background: "rgba(168,85,247,0.15)", color: "#a855f7", border: "1px solid #a855f755", fontSize: 9, padding: "2px 7px", borderRadius: 3, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>AI OCR</span>
      </div>

      {!extraction && (
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          htmlFor="coi-file-input"
          data-testid="coi-dropzone"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            padding: "30px 20px",
            border: `2px dashed ${drag ? "#a855f7" : "#1e2530"}`,
            borderRadius: 10,
            background: drag ? "rgba(168,85,247,0.06)" : "#080c12",
            color: "#94a3b8",
            cursor: uploading ? "wait" : "pointer",
            transition: "all 0.18s ease",
          }}
        >
          <input
            ref={inputRef}
            id="coi-file-input"
            data-testid="coi-file-input"
            type="file"
            accept={ACCEPT}
            disabled={uploading}
            onChange={(e) => handleFile(e.target.files?.[0])}
            style={{ display: "none" }}
          />
          {uploading ? (
            <>
              <Loader2 className="animate-spin" size={28} style={{ color: "#a855f7" }} />
              <div style={{ fontSize: 13, color: "#f1f5f9", fontWeight: 600 }}>{step || "Working..."}</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>{filename}</div>
            </>
          ) : (
            <>
              <UploadCloud size={34} style={{ color: "#a855f7" }} />
              <div style={{ fontSize: 13, color: "#f1f5f9", fontWeight: 600 }}>Drop COI PDF or image here</div>
              <div style={{ fontSize: 11, color: "#64748b" }}>or click to browse · PDF, JPG, PNG, WEBP up to {MAX_MB}MB</div>
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>Claude Vision extracts the CIN → auto-runs MCA21 lookup</div>
            </>
          )}
        </label>
      )}

      {extraction && (
        <div style={{ background: "#080c12", border: `1px solid ${extraction.cin ? "#22c55e55" : "#f59e0b55"}`, borderRadius: 8, padding: 14 }} data-testid="coi-extraction-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {extraction.cin ? (
                <CheckCircle2 size={16} style={{ color: "#22c55e" }} />
              ) : (
                <AlertTriangle size={16} style={{ color: "#f59e0b" }} />
              )}
              <strong style={{ color: "#f1f5f9", fontSize: 13 }}>
                {extraction.cin ? "CIN Extracted" : "No CIN Found"}
              </strong>
              <span style={{ color: "#94a3b8", fontSize: 11 }}>· {filename}</span>
            </div>
            <button onClick={reset} data-testid="coi-reset-btn" style={{ background: "transparent", border: "1px solid #1e2530", borderRadius: 6, color: "#94a3b8", padding: "5px 10px", fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <X size={11} /> Upload another
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Field label="CIN" value={extraction.cin} mono color={extraction.cin ? "#22c55e" : "#94a3b8"} />
            <Field label="Company Name" value={extraction.company_name} />
            <Field label="Type" value={extraction.company_type} />
            <Field label="Incorporated" value={extraction.incorporation_date} />
            <Field label="Registrar" value={extraction.registrar} />
            <Field label="State" value={extraction.state} />
            <Field label="OCR Confidence" value={extraction.confidence != null ? `${Math.round(extraction.confidence * 100)}%` : "—"}
                   color={(extraction.confidence || 0) > 0.7 ? "#22c55e" : "#f59e0b"} />
          </div>

          {extraction.notes && (
            <div style={{ marginTop: 12, padding: 10, background: "rgba(37,99,235,0.06)", border: "1px solid #2563eb33", borderRadius: 6, color: "#cbd5e1", fontSize: 11, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Sparkles size={11} style={{ color: "#3b82f6", marginTop: 2, flexShrink: 0 }} />
              <div>{extraction.notes}</div>
            </div>
          )}

          {extraction.cin && (
            <div style={{ marginTop: 12, padding: 10, background: "rgba(34,197,94,0.06)", border: "1px solid #22c55e33", borderRadius: 6, color: "#22c55e", fontSize: 11, display: "flex", gap: 6, alignItems: "center" }}>
              <FileText size={12} /> MCA21 lookup was triggered automatically — see results below.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono, color }) {
  return (
    <div>
      <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>{label}</div>
      <div style={{ color: color || "#f1f5f9", fontSize: 12, fontWeight: 600, fontFamily: mono ? "ui-monospace, monospace" : "inherit", wordBreak: "break-word" }}>
        {value || "—"}
      </div>
    </div>
  );
}
